import React, { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  AppBar,
  Box,
  Divider,
  Hidden,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListSubheader,
  Paper,
  Popover,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Button from "../CustomButtons/Button";
import ViewList from "@mui/icons-material/ViewList";

import MoreVert from "@mui/icons-material/MoreVert";
import { withStyles } from "tss-react/mui";
import adminNavbarStyle from "../../assets/jss/material-dashboard-pro-react/components/adminNavbarStyle";
import * as actions from "../../store/actions";
import TranslateIcon from "@mui/icons-material/Translate";
import { useDialog } from "../../licensing/utils/DialogProvider";
import TranslationDialogSetLanguage from "../TranslationWrapper/TranslationDialogSetLanguage";
import TranslationSideBar from "../TranslationWrapper/TranslationSideBar";
import { AdjustOutlined, Lock, Menu } from "@mui/icons-material";
import AuthenticationWrapper from "../../auth/AuthenticationWrapper";
import { authenticationMode } from "../../store/reducers/permissions";
import axios from "axios";

const rex = /(?<id>\d+):(?<name>.*)/;

function parseSalesareaKeyName(salesareaKeyname) {
  return salesareaKeyname.match(rex).groups;
}

function SalesareaListItem({ name, id }) {
  const dispatch = useDispatch();

  return (
    <ListItem
      disablePadding
      secondaryAction={
        <a href={`/manager/vestigingen/${id}/edit`} rel={"noreferrer"} style={{ justifySelf: "end" }} target={"_blank"}>
          Open
        </a>
      }
      value={id}
    >
      <ListItemButton
        onClick={() => {
          dispatch({
            type: actions.SET_FILTER,
            payload: {
              key: "vestiging_id",
              filter: { field: "vestiging_id", operator: "=", value: id, label: name },
            },
          });
        }}
      >
        <ListItemIcon style={{ color: "gray" }}>#{id}</ListItemIcon>
        <span
          style={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            maxWidth: "12em",
            paddingRight: "4px",
          }}
        >
          {name}
        </span>
      </ListItemButton>
    </ListItem>
  );
}

function useLoadSalesareasByVenue(venueId) {
  const [salesareasByVenue, setSalesareasByVenue] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (venueId) {
      (async () => {
        setLoading(true);
        try {
          const response = await axios.post(`core/vestigingen/search?fields=id,naam&limit=50`, [
            {
              field: "venue_id",
              operator: "=",
              value: venueId,
            },
          ]);
          if (response.data.data.data.length) {
            setSalesareasByVenue(response.data.data.data);
          }
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setSalesareasByVenue([]);
    }
  }, [venueId]);

  return {
    loading,
    salesareasByVenue,
  };
}

function SalesareaSelect() {
  const filters = useSelector((state) => state.filtersV2);
  const venueId = filters?.["venue_id"]?.["value"];
  const theme = useTheme();
  const isSmallWidth = useMediaQuery(theme.breakpoints.down("md"));

  let name = filters?.["vestiging_id"]?.["label"];
  let id = filters?.["vestiging_id"]?.["value"];
  if (Array.isArray(name)) {
    name = name[0];
  }
  if (Array.isArray(id)) {
    id = id[0];
  }
  const [anchor, setAnchor] = useState(null);

  const { loading, salesareasByVenue } = useLoadSalesareasByVenue(venueId);

  let accessesRaw = localStorage.getItem("salesareaAccess") ?? `{"counter": {}, "recent": []}`;
  let accesses = { counter: {}, recent: [] };
  try {
    accesses = JSON.parse(accessesRaw);
  } catch (e) {}

  return (
    <AuthenticationWrapper permissionKey={"backoffice-powertools-salesarea-switcher"} usePrefix={false}>
      <Popover
        PaperProps={{
          sx: {
            gridArea: "menu",
            padding: "16px",
            maxWidth: "80vw",
            maxHeight: "80vh",
            overflowY: "scroll",
          },
        }}
        anchorEl={anchor}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onClose={() => setAnchor(null)}
        open={Boolean(anchor)}
      >
        <Typography sx={{ gridArea: "title", fontSize: "1.3em" }} variant={"h2"}>
          Salesarea quick switcher
        </Typography>
        <Divider />
        <Box
          sx={(theme) => ({
            [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
            [theme.breakpoints.up("md")]: { gridTemplateColumns: "repeat(3, 1fr)" },
            display: "grid",
            gap: "8px",
            width: "100%",
          })}
        >
          <List
            dense
            disablePadding
            subheader={<ListSubheader>Recent</ListSubheader>}
            sx={{ bgcolor: "background.paper" }}
          >
            {accesses["recent"]
              .map((key) => parseSalesareaKeyName(key))
              .map(({ id, name }) => (
                <SalesareaListItem id={id} name={name} />
              ))}
          </List>

          <List dense disablePadding subheader={<ListSubheader>Most used</ListSubheader>}>
            {Object.entries(accesses["counter"])
              .sort((a, b) => a[1] - b[1])
              .map((pair) => parseSalesareaKeyName(pair[0]))
              .slice(0, 10)
              .map(({ id, name }) => (
                <SalesareaListItem id={id} name={name} />
              ))}
          </List>
          <List dense disablePadding subheader={<ListSubheader>This venue</ListSubheader>}>
            {loading ? (
              "Loading"
            ) : venueId == null ? (
              <ListItem disabled>No venue selected</ListItem>
            ) : (
              salesareasByVenue.map(({ id, naam }) => <SalesareaListItem id={id} name={naam} />)
            )}
          </List>
        </Box>
      </Popover>
      <Paper
        sx={(theme) => ({
          p: 1,
          transition: "all 200ms ease-in",
          top: "-4px",
          position: "relative",
          [theme.breakpoints.up("md")]: {
            marginRight: "16px",
            gap: "2px",
            display: "grid",
            gridTemplate: `
      "name menu"
      "link menu"
      `,
            maxWidth: "200px",
          },
          [theme.breakpoints.down("md")]: {
            borderRadius: "50%",
            maxWidth: "41px",
            maxHeight: "41px",
          },
        })}
      >
        {isSmallWidth ? (
          <IconButton
            onClick={(e) => {
              setAnchor(e.currentTarget);
            }}
            style={{ padding: 0 }}
          >
            <AdjustOutlined />
          </IconButton>
        ) : (
          <>
            <div style={{ gridArea: "name" }}>Salesarea</div>
            <Box
              component={"a"}
              href={`/manager/vestigingen/${id}/edit`}
              sx={(theme) => ({
                display: "inline-block",
                gridArea: "link",
                [theme.breakpoints.up("md")]: {
                  marginRight: "16px",
                  maxWidth: "15em",
                },
                [theme.breakpoints.down("md")]: {
                  marginRight: "8px",
                  maxWidth: "5em",
                },
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
              })}
              target={"_blank"}
              title={`#${id} - ${name}`}
            >
              <span>{id ? `#${id} - ${name}` : "None selected"}</span>
            </Box>
            <IconButton style={{ gridArea: "menu", alignSelf: "center" }}>
              <AdjustOutlined
                onClick={(e) => {
                  setAnchor(e.currentTarget);
                }}
              />
            </IconButton>
          </>
        )}
      </Paper>
    </AuthenticationWrapper>
  );
}

function MainNavbar({ ...props }) {
  const { classes, translationMode } = props;
  const [openDialog] = useDialog();
  const translateExceptionsMode = JSON.parse(localStorage.getItem("translateExceptionsMode")) ?? false;

  return (
    <>
      {translateExceptionsMode && translationMode ? <TranslationSideBar /> : null}
      <AppBar className={classes.appBar}>
        <Toolbar className={classes.container}>
          <Hidden implementation={"css"} smDown>
            <div className={classes.sidebarMinimize}>
              {props.miniActive ? (
                <Button color={"white"} justIcon onClick={props.toggleSidebar} round>
                  <ViewList className={classes.sidebarMiniIcon} />
                </Button>
              ) : (
                <Button color={"white"} justIcon onClick={props.toggleSidebar} round>
                  <MoreVert className={classes.sidebarMiniIcon} />
                </Button>
              )}
            </div>
          </Hidden>
          <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
            {/* Here we create navbar brand, based on route name */}
            <Typography style={{ textTransform: "none", padding: "0 30px" }} variant={"h6"}>
              {props.paginaTitel}
            </Typography>
            <Typography style={{ fontWeight: 800 }} variant={"h6"}>
              {props.subject}
            </Typography>
          </div>
          <div>{props.children}</div>
          <div
            style={{
              display: "flex",
              gap: "2px",
            }}
          >
            <SalesareaSelect />
            <AuthenticationWrapper
              child={() => (
                <Button
                  className={classes.appResponsive}
                  color={authenticationMode ? "orange" : "white"}
                  justIcon
                  onClick={() => {
                    const authMode = !(localStorage.getItem("authenticationMode") === "true");
                    localStorage.setItem("authenticationMode", authMode ? "true" : "false");
                    location.reload();
                  }}
                  round
                  style={{ backgroundColor: authenticationMode ? "#ff9800" : null }}
                >
                  <Lock />
                </Button>
              )}
              permissionKey={"authenticationMode"}
              usePrefix={false}
            />
            <AuthenticationWrapper
              child={() => (
                <Button
                  className={classes.appResponsive}
                  color={translationMode ? "orange" : "white"}
                  justIcon
                  onClick={() => {
                    openDialog({
                      children: <TranslationDialogSetLanguage />,
                      fullWidth: true,
                      maxWidth: "xs",
                    });
                  }}
                  round
                  style={{ backgroundColor: translationMode ? "#ff9800" : null }}
                >
                  <TranslateIcon />
                </Button>
              )}
              permissionKey={"translationMode"}
              usePrefix={false}
            />
            <Hidden implementation={"css"} smUp>
              <Button
                aria-label={"open drawer"}
                className={classes.appResponsive}
                color={"transparent"}
                justIcon
                onClick={props.toggleMobile}
              >
                <Menu />
              </Button>
            </Hidden>
          </div>
        </Toolbar>
      </AppBar>
    </>
  );
}

//REDUX
const mapStateToProps = (state) => {
  return {
    miniActive: state.app.sidebarMiniActive,
    translationMode: state.translations.translationMode,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    toggleSidebar: () => dispatch({ type: actions.TOGGLE_SIDEBAR }),
    toggleMobile: () => dispatch({ type: actions.TOGGLE_MOBILE }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(MainNavbar, adminNavbarStyle));
